import React, { useEffect, useState } from "react"
import { handleAuthenticate } from "@/api/auth.api"
import {
  IAuthContext,
  INeedsAuthentication,
  IUserCredentials,
} from "@/types/auth"
import {
  retrieveUserToken,
  removeUserTokenFromSession,
  storeUserToken,
} from "@/utils/secureStorage"
import { Typography, Modal } from "antd"
import LoginForm from "@components/forms/login"
import Axios from "axios"
import useapi from "@/hooks/useapi"
import keycloak from "@/keycloak-config"

const { Title } = Typography

const defaultState: IAuthContext = {
  userName: null,
  token: null,
  authenticate: async () => {},
  authenticateWithKeycloak: async () => {},
  removeUserToken: () => null,
  setNeedsReAuthentication: () => null,
  error: null,
  loading: false,
  handleNeedsAuthentication: () => null,
  isKeycloakAuth: false,
}

const defaultNeedsAuthentication: INeedsAuthentication = {
  visible: false,
  path: "",
  apiFn: () => null,
}

const AuthContext = React.createContext<IAuthContext>(defaultState)

const AuthContextProvider = ({ children }: { children: any }) => {
  const [userName, setUserName] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(retrieveUserToken())
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [isKeycloakAuth, setIsKeycloakAuth] = useState<boolean>(false)

  const [needsReAuthentication, setNeedsReAuthentication] =
    useState<INeedsAuthentication>(defaultNeedsAuthentication)

  useEffect(() => {
    const userToken = retrieveUserToken()
    setToken(userToken)
  }, [])

  // Initialize Keycloak on mount
  useEffect(() => {
    const initKeycloak = async () => {
      try {
        const authenticated = await keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          pkceMethod: 'S256'
        });

        if (authenticated) {
          const tokenPayload = keycloak.tokenParsed;
          setIsKeycloakAuth(true);
          setUserName(tokenPayload?.preferred_username || keycloak.subject);
          storeUserToken(keycloak.token);
          setToken(keycloak.token);
        }
      } catch (error) {
        console.error('Failed to initialize Keycloak:', error);
      }
    };

    initKeycloak();
  }, []);

  const handleNeedsAuthentication = (
    visible: boolean,
    path: string,
    apiFn: any,
  ) => {
    setNeedsReAuthentication({ visible, path, apiFn })
  }

  const authenticate = async (data: IUserCredentials) => {
    try {
      setLoading(true)
      setError(null)

      // Check if credentials match admin credentials
      if (
        data.userName === process.env.REACT_APP_ADMIN_USERNAME &&
        data.password === process.env.REACT_APP_ADMIN_PASSWORD
      ) {
        // Create a simple admin token with base64 encoding
        const payload = {
          sub: data.userName,
          name: 'Admin User',
          role: 'admin',
          exp: Math.floor(Date.now() / 1000) + (60 * 60), // 1 hour expiration
        };
        
        // Create a JWT-like structure with base64 encoding
        const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
        const body = btoa(JSON.stringify(payload));
        const adminToken = `${header}.${body}.`;

        setUserName(data.userName)
        setToken(adminToken)
        storeUserToken(adminToken)
        setIsKeycloakAuth(false)
        setLoading(false)
        return
      }

      // If not admin, try regular authentication
      const token = await handleAuthenticate(data)
      setUserName(data.userName)
      setToken(token!)
      setIsKeycloakAuth(false)
    } catch (e) {
      if (Axios.isAxiosError(e)) {
        setError(e.message)
      } else {
        setError('Invalid credentials')
      }
      setLoading(false)
    }
    setLoading(false)
  }

  const authenticateWithKeycloak = async () => {
    try {
      setLoading(true);
      await keycloak.login({
        redirectUri: window.location.origin
      });
    } catch (error) {
      console.error('Failed to login with Keycloak:', error);
      setError('Failed to login with AURIN SSO');
    } finally {
      setLoading(false);
    }
  };

  const removeUserToken = () => {
    removeUserTokenFromSession()
    setToken(null)
    if (isKeycloakAuth) {
      keycloak.logout({
        redirectUri: window.location.origin
      });
    }
  }

  /**
   * Modal to display when the api is greeted with 403
   */
  const renderAuthModal = () => {
    return (
      <Modal
        title="Authenticate - Token Expired"
        visible={needsReAuthentication.visible}
        style={{ minHeight: 500 }}
        footer={null}
        destroyOnClose
        onCancel={() =>
          setNeedsReAuthentication({ ...defaultNeedsAuthentication })
        }
      >
        <Title level={5} style={{ textAlign: "center", paddingBottom: 10 }}>
          Login
        </Title>
        <LoginForm
          onFinish={async (data: IUserCredentials) => {
            await authenticate(data)
            setNeedsReAuthentication(() => ({
              ...defaultNeedsAuthentication,
            }))
            if (needsReAuthentication.apiFn) {
              await needsReAuthentication.apiFn()
            }
          }}
          loading={loading}
        />
      </Modal>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        userName,
        token: token ? `Bearer ${token}` : null, // Add Bearer prefix when providing token to components
        error,
        loading,
        authenticate,
        authenticateWithKeycloak,
        removeUserToken,
        setNeedsReAuthentication,
        handleNeedsAuthentication,
        isKeycloakAuth,
      }}
    >
      {renderAuthModal()}
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext
export { AuthContextProvider }
